import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useTheme,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  styled,
  Chip,
  Tooltip,
  Toolbar,
  Checkbox,
  Avatar,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination
} from "@mui/material";
// Additional components for sidebar navigation
import { List, ListItem, ListItemIcon, ListItemText, Divider } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
// Icons for sidebar items and toggle
import ContactsIcon from '@mui/icons-material/Contacts';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import ArchiveIcon from '@mui/icons-material/Archive';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PrintIcon from '@mui/icons-material/Print';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import SearchIcon from "@mui/icons-material/Search";
import { useState, useEffect, useMemo, useCallback } from "react";
import { getUserConfig } from '../../services/config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../services/auth';
import { getFavorites, addFavorite, removeFavorite } from '../../services/favoriteService';
import Joyride, { STATUS, EVENTS } from 'react-joyride';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const Contacts = () => {

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFideliteEnabled = true; // À remplacer par la configuration réelle

  // États pour la gestion des profils avec pagination
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Segment filtering state
  const [segmentFilter, setSegmentFilter] = useState(null);
  const [segmentName, setSegmentName] = useState(null);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortBy, setSortBy] = useState('Date Created');
  const [sortModel, setSortModel] = useState([{ field: 'dateCreated', sort: 'desc' }]);
  const [menuPosition, setMenuPosition] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  // Sidebar open/close state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Filter state for sidebar
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' or 'favorites'

  // Tour state
  const [runTour, setRunTour] = useState(false);

  // Confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Bulk deletion state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeletionProgress, setBulkDeletionProgress] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteAction, setBulkDeleteAction] = useState('');

  // Handle URL parameters for segment filtering
  useEffect(() => {
    const segmentId = searchParams.get('segment');
    const segmentNameParam = searchParams.get('segmentName');

    if (segmentId) {
      setSegmentFilter(segmentId);
      setSegmentName(segmentNameParam || `Segment ${segmentId}`);
    } else {
      setSegmentFilter(null);
      setSegmentName(null);
    }

    // Mark URL parameters as processed
    setUrlParamsProcessed(true);
  }, [searchParams]);

  // Load favorites from backend
  const loadFavorites = async () => {
    setFavoritesLoading(true);
    try {
      const favoritesData = await getFavorites('profile');
      const favoriteIds = new Set(favoritesData.map(fav => fav.data_id));
      setFavorites(favoriteIds);
    } catch (err) {
      console.error('Error loading favorites:', err);
    } finally {
      setFavoritesLoading(false);
    }
  };

  // Load favorites on component mount
  useEffect(() => {
    loadFavorites();
  }, []);

  // Sidebar menu items
  const sidebarItems = useMemo(() => [
    { label: 'Tous les contacts', icon: <ContactsIcon fontSize="small" />, filter: 'all' },
    { label: 'Favoris', icon: <StarBorderIcon fontSize="small" />, filter: 'favorites' },
  ], []);

  // Calculate offset based on current page
  const calculateOffset = (page, limit) => {
    return (page - 1) * limit;
  };

  // Handle sidebar filter change
  const handleSidebarItemClick = (item) => {
    if (item.filter === activeFilter) return; // Already on this filter

    setActiveFilter(item.filter);
    setCurrentPage(1); // Reset to first page
    setSearchTerm(''); // Clear search when changing filter

    // Clear segment filter when changing main filter
    if (segmentFilter) {
      setSearchParams({});
      setSegmentFilter(null);
      setSegmentName(null);
    }
  };

  // Menu handlers
  const handleMenuClick = (event, profile) => {
    event.preventDefault();
    setMenuPosition({ mouseX: event.clientX + 2, mouseY: event.clientY + 2 });
    setSelectedProfile(profile);
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedProfile(null);
  };

  // Favorite toggle with backend integration
  const toggleFavorite = async (profileId) => {
    const isFavorite = favorites.has(profileId);

    // Optimistically update UI
    const newFavorites = new Set(favorites);
    if (isFavorite) {
      newFavorites.delete(profileId);
    } else {
      newFavorites.add(profileId);
    }
    setFavorites(newFavorites);

    // If we're on favorites filter and removing a favorite, also remove from profiles list
    if (activeFilter === 'favorites' && isFavorite) {
      setProfiles(prevProfiles => prevProfiles.filter(p => p.itemId !== profileId));
      setTotalRows(prev => prev - 1);
      setTotalPages(prev => Math.ceil((prev * pageSize - 1) / pageSize));
    }

    try {
      if (isFavorite) {
        // Remove from favorites
        await removeFavorite('profile', profileId);
      } else {
        // Add to favorites
        await addFavorite('profile', profileId);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      // Revert optimistic update on error
      setFavorites(favorites);

      // Revert profile list changes if we modified it
      if (activeFilter === 'favorites' && isFavorite) {
        // Reload to get the correct state
        loadProfiles(currentPage, pageSize, searchTerm, segmentFilter);
      }

      setError('Erreur lors de la mise à jour des favoris');
    }
  };

  // Delete confirmation handlers
  const handleDeleteClick = (profile) => {
    setProfileToDelete(profile);
    setDeleteConfirmOpen(true);
    handleMenuClose(); // Close the context menu if open
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setProfileToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!profileToDelete) return;

    setIsDeleting(true);
    try {
      await api.delete(`/profiles/${profileToDelete.itemId}`);
      // Reload profiles after deletion
      loadProfiles(currentPage, pageSize, searchTerm, segmentFilter);
      setDeleteConfirmOpen(false);
      setProfileToDelete(null);
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError('Échec de la suppression du contact');
      // Keep dialog open to show error, or you can close it
      setDeleteConfirmOpen(false);
      setProfileToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  // Bulk delete handlers
  const handleBulkDeleteClick = () => {
    if (selectedRows.length === 0) {
      setError('Veuillez sélectionner au moins un contact à supprimer');
      return;
    }
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteCancel = () => {
    if (!isBulkDeleting) {
      setBulkDeleteDialogOpen(false);
      setBulkDeletionProgress([]);
      setError(null); // Clear any errors when closing
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedRows.length === 0) return;

    setIsBulkDeleting(true);
    setError(null);

    // Initialize progress for all selected profiles
    const initialProgress = selectedRows.map(profileId => {
      const profile = profiles.find(p => p.itemId === profileId);
      return {
        profile_id: profileId,
        name: profile ? `${profile.properties?.firstName || ''} ${profile.properties?.lastName || ''}`.trim() || 'Unknown' : 'Unknown',
        email: profile?.properties?.email || 'N/A',
        status: 'pending',
        mautic_sync: { attempted: false, success: false, message: null },
        error: null
      };
    });
    setBulkDeletionProgress(initialProgress);

    try {
      const response = await api.post('/profiles/batch/delete', {
        profile_ids: selectedRows
      });

      const data = response.data;

      // Update progress with results
      setBulkDeletionProgress(data.results || []);

      // Don't auto-close - let user review results and close manually
      if (data.success) {
        // All deletions successful - reload profiles but keep dialog open
        loadProfiles(currentPage, pageSize, searchTerm, segmentFilter);
        setSelectedRows([]);
      } else {
        // Some deletions failed - show error
        setError(data.error || 'Certaines suppressions ont échoué');
      }

    } catch (err) {
      console.error('Error in bulk deletion:', err);
      setError('Erreur lors de la suppression groupée: ' + (err.response?.data?.detail || err.message));

      // If we got partial results from the error response
      if (err.response?.data?.results) {
        setBulkDeletionProgress(err.response.data.results);
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkActionChange = (event) => {
    const action = event.target.value;
    setBulkDeleteAction(action);

    if (action === 'delete') {
      handleBulkDeleteClick();
      // Reset the select after opening dialog
      setTimeout(() => setBulkDeleteAction(''), 100);
    }
  };

  // Export selected contacts to CSV
  const handleExportCSV = async () => {
    if (selectedRows.length === 0) {
      setError('Veuillez sélectionner au moins un contact à exporter');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/profiles/export', {
        profile_ids: selectedRows
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `contacts_export_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Clear selection after export
      setSelectedRows([]);

    } catch (err) {
      console.error('Error exporting contacts:', err);
      setError('Erreur lors de l\'exportation des contacts: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '?';
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  // Get random color for avatar based on name
  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0288d1', '#689f38', '#fbc02d'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Configuration des colonnes
  const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#f8f9fa',
      color: '#495057',
      borderBottom: '1px solid #dee2e6',
      fontWeight: 600,
    },
    '& .MuiDataGrid-cell': {
      borderBottom: '1px solid #f1f3f4',
      padding: '12px 16px',
      whiteSpace: 'normal',
      lineHeight: 1.2,
      alignItems: 'flex-start',
      '&:focus': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-row': {
      '&:hover': {
        backgroundColor: '#f8f9fa',
      },
      '&.Mui-selected': {
        backgroundColor: '#e3f2fd',
        '&:hover': {
          backgroundColor: '#bbdefb',
        },
      },
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: '1px solid #dee2e6',
      backgroundColor: '#f8f9fa',
    },
    '& .MuiCheckbox-root': {
      color: '#1976d2',
      padding: 0,
      margin: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
  }));

  const columns = useMemo(() => [
    {
      field: 'select',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <Checkbox
          size="small"
          inputProps={{ 'aria-label': 'Sélectionner toutes les lignes' }}
          indeterminate={selectedRows.length > 0 && selectedRows.length < profiles.length}
          checked={profiles.length > 0 && selectedRows.length === profiles.length}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows(profiles.map(p => p.itemId));
            } else {
              setSelectedRows([]);
            }
          }}
          sx={{
            color: '#1976d2',
            '&.Mui-checked': {
              color: '#1976d2',
            },
            '&.MuiCheckbox-indeterminate': {
              color: '#1976d2',
            },
          }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          size="small"
          inputProps={{ 'aria-label': 'Sélectionner la ligne' }}
          checked={selectedRows.includes(params.row.itemId)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, params.row.itemId]);
            } else {
              setSelectedRows(selectedRows.filter(id => id !== params.row.itemId));
            }
          }}
          sx={{
            color: '#1976d2',
            '&.Mui-checked': {
              color: '#1976d2',
            },
          }}
        />
      ),
    },
    {
      field: 'favorite',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label={favorites.has(params.row.itemId) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite(params.row.itemId);
          }}
          sx={{ color: favorites.has(params.row.itemId) ? '#ffc107' : '#6c757d' }}
        >
          {favorites.has(params.row.itemId) ?
            <StarIcon fontSize="small" /> :
            <StarBorderIcon fontSize="small" />
          }
        </IconButton>
      ),
    },
    {
      field: 'name',
      headerName: 'Nom',
      flex: 1,
      minWidth: 200,
      sortable: true,
      disableColumnMenu: true,
      valueGetter: (params) => {
        const firstName = params.row.properties?.firstName || '';
        const lastName = params.row.properties?.lastName || '';
        return `${firstName} ${lastName}`.trim().toLowerCase();
      },
      renderCell: (params) => {
        const firstName = params.row.properties?.firstName || '';
        const lastName = params.row.properties?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';
        const initials = getInitials(firstName, lastName);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '14px',
                backgroundColor: getAvatarColor(fullName),
              }}
            >
              {initials}
            </Avatar>
            <Typography variant="body2" fontWeight={500}>
              {fullName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Adresse e-mail',
      flex: 1,
      minWidth: 200,
      sortable: true,
      disableColumnMenu: true,
      valueGetter: (params) => (params.row.properties?.email || 'N/A').toLowerCase(),
    },
    {
      field: 'phone',
      headerName: 'Téléphone',
      flex: 0.8,
      minWidth: 120,
      sortable: true,
      disableColumnMenu: true,
      valueGetter: (params) => (params.row.properties?.phone || params.row.properties?.phoneNumber || 'N/A').toLowerCase(),
    },
    {
      field: 'dateCreated',
      headerName: 'Date de création',
      flex: 0.8,
      minWidth: 120,
      sortable: true,
      disableColumnMenu: true,
      type: 'date',
      valueGetter: (params) => new Date(params.row.properties?.firstVisit || params.row.properties?.createdDate || 0),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Voir les détails">
            <IconButton
              size="small"
              aria-label="Voir les détails"
              onClick={() => navigate(`/contacts/${params.row.itemId}`)}
              sx={{ color: '#6c757d' }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Modifier">
            <IconButton
              size="small"
              aria-label="Modifier le contact"
              onClick={() => navigate(`/contacts/${params.row.itemId}/edit`)}
              sx={{ color: '#6c757d' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton
              size="small"
              aria-label="Supprimer le contact"
              onClick={() => handleDeleteClick(params.row)}
              sx={{ color: '#6c757d' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Plus d'options">
            <IconButton
              size="small"
              aria-label="Plus d'options"
              onClick={(event) => handleMenuClick(event, params.row)}
              sx={{ color: '#6c757d' }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [selectedRows, profiles, favorites, navigate, currentPage, pageSize, searchTerm, segmentFilter]);

  // Helper: deduplicate a profile list by itemId (safety net for Unomi ghost profiles)
  const dedupeByItemId = (list) => {
    const seen = new Set();
    return list.filter((p) => {
      if (!p.itemId) return true; // keep profiles with no id
      if (seen.has(p.itemId)) return false;
      seen.add(p.itemId);
      return true;
    });
  };

  // Fonction pour charger les profils avec pagination
  const loadProfiles = async (page = 1, limit = 10, search = '', segmentId = null) => {
    setLoading(true);
    setError(null);

    try {
      // If filtering by favorites, we need to filter the results
      if (activeFilter === 'favorites') {
        // Get all favorite profile IDs
        const favoriteIds = Array.from(favorites);

        if (favoriteIds.length === 0) {
          // No favorites, show empty list
          setProfiles([]);
          setTotalRows(0);
          setTotalPages(0);
          setLoading(false);
          return;
        }

        // Load profiles and filter by favorites
        // We'll need to fetch all profiles that match the search and then filter by favorites
        // For now, we'll fetch with a larger limit and filter client-side
        // TODO: Implement server-side favorites filtering for better performance

        const offset = calculateOffset(page, limit);

        const requestData = {
          offset: 0, // Get all to filter
          limit: 1000, // Fetch more to filter
          search_term: search.trim() || null,
          segment_id: segmentId,
          sort_by: 'properties.firstVisit:desc'
        };

        const response = await api.post('/profiles/search', requestData);
        const data = response.data;

        // Filter profiles by favorites
        const allProfiles = dedupeByItemId(data.profiles || []);
        const filteredProfiles = allProfiles.filter(profile => favorites.has(profile.itemId));

        // Apply client-side pagination
        const total = filteredProfiles.length;
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedProfiles = filteredProfiles.slice(startIndex, endIndex);

        console.log(`Favorites filter: ${paginatedProfiles.length} profiles on page ${page}, total: ${total}`);

        setProfiles(paginatedProfiles);
        setTotalRows(total);
        setTotalPages(Math.ceil(total / limit));
        setSelectedRows([]);
      } else {
        // Normal loading without favorites filter
        const offset = calculateOffset(page, limit);

        console.log(`Loading page ${page} with offset: ${offset}, limit: ${limit}`);

        const requestData = {
          offset: offset,
          limit: limit,
          search_term: search.trim() || null,
          segment_id: segmentId,
          sort_by: 'properties.firstVisit:desc'
        };

        console.log('Request data:', requestData);

        const response = await api.post('/profiles/search', requestData);
        const data = response.data;

        const profilesList = dedupeByItemId(data.profiles || []);
        const total = data.total || 0;

        console.log(`Received ${profilesList.length} profiles, total: ${total}`);

        setProfiles(profilesList);
        setTotalRows(total);
        setTotalPages(Math.ceil(total / limit));
        setSelectedRows([]);
      }

    } catch (err) {
      console.error('Erreur détaillée lors du chargement des profils:', err);
      setError("Erreur lors du chargement des profils: " + (err.response?.data?.detail || err.message));
      setProfiles([]);
      setTotalRows(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    loadProfiles(newPage, pageSize, searchTerm, segmentFilter);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
    loadProfiles(1, newPageSize, searchTerm, segmentFilter);
  };

  // Handle search with debounce effect
  useEffect(() => {
    // Only run if URL parameters have been processed
    if (!urlParamsProcessed) return;

    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
      loadProfiles(1, pageSize, searchTerm, segmentFilter);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, pageSize, segmentFilter, urlParamsProcessed, activeFilter]); // Removed favorites from dependencies

  // Load initial data once after URL processing
  useEffect(() => {
    // Only load if URL parameters have been processed
    if (!urlParamsProcessed) return;

    // Reset to first page for initial load
    setCurrentPage(1);
    loadProfiles(1, pageSize, searchTerm, segmentFilter);
  }, [urlParamsProcessed]); // Only run once after URL processing

  // Calculate display range
  const startIndex = ((currentPage - 1) * pageSize) + 1;
  const endIndex = Math.min(currentPage * pageSize, totalRows);

  // Tour steps configuration
  const tourSteps = useMemo(() => [
    {
      target: '[data-tourid="sidebar-toggle"]',
      content: 'Utilisez ce bouton pour afficher ou masquer la barre latérale. La barre latérale contient les filtres et le bouton pour ajouter un nouveau contact.',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tourid="new-contact-button"]',
      content: 'Cliquez ici pour ajouter un nouveau contact. Ce bouton n\'est visible que lorsque la barre latérale est ouverte.',
      placement: 'bottom',
    },
    {
      target: '[data-tourid="sidebar-filters"]',
      content: 'Utilisez ces filtres pour afficher tous les contacts ou uniquement vos favoris. Cliquez sur un filtre pour l\'activer.',
      placement: 'right',
    },
    {
      target: '[data-tourid="search-contacts"]',
      content: 'Utilisez cette barre de recherche pour trouver rapidement un contact par nom, email ou téléphone.',
      placement: 'bottom',
      disableBeacon: true,
      spotlightPadding: 5,
      offset: 10,
    },
    {
      target: '[data-tourid="bulk-actions"]',
      content: 'Sélectionnez plusieurs contacts en cochant les cases, puis utilisez ce menu pour effectuer des actions groupées comme la suppression ou l\'export.',
      placement: 'bottom',
    },
    {
      target: '[data-tourid="contacts-grid"]',
      content: 'Voici la liste de tous vos contacts. Cliquez sur un contact pour voir ses détails, ou utilisez les icônes pour modifier, supprimer ou ajouter aux favoris.',
      placement: 'center',
    },
    {
      target: '[data-tourid="pagination"]',
      content: 'Utilisez la pagination pour naviguer entre les pages de contacts. Vous pouvez également modifier le nombre d\'éléments par page.',
      placement: 'top',
    },
  ], []);

  // Handle tour callbacks
  const handleTourCallback = useCallback((data) => {
    const { status, type, index } = data;

    console.log('Tour callback:', { status, type, index, step: tourSteps[index]?.target });

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      // Save to localStorage that user has completed the tour
      localStorage.setItem('contacts-tour-completed', 'true');
    } else if (type === EVENTS.STEP_BEFORE) {
      // Ensure sidebar is open for steps that require it (should already be open from tour start)
      if ((index === 1 || index === 2) && !isSidebarOpen) {
        setIsSidebarOpen(true);
      }
    } else if (type === EVENTS.TARGET_NOT_FOUND) {
      // If target not found, log it but let joyride handle it
      console.warn('Target not found for step', index, 'target:', tourSteps[index]?.target);
    }
  }, [tourSteps, isSidebarOpen]);

  // Check if user has completed tour before, and offer to restart
  useEffect(() => {
    const tourCompleted = localStorage.getItem('contacts-tour-completed');
    // Auto-start tour for first-time users
    if (!tourCompleted) {
      // Open sidebar before starting tour to ensure elements are in DOM
      if (!isSidebarOpen) {
        setIsSidebarOpen(true);
      }
      setTimeout(() => setRunTour(true), 1500);
    }
  }, [isSidebarOpen]);

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {/* Joyride Tour Component */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        callback={handleTourCallback}
        disableOverlayClose={false}
        disableScrolling={false}
        spotlightClicks={false}
        floaterProps={{
          disableAnimation: false,
        }}
        scrollOffset={200}
        scrollToFirstStep={true}
        scrollDuration={300}
        styles={{
          options: {
            primaryColor: '#00838f',
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: 8,
            fontFamily: 'Montserrat, Arial, sans-serif',
            backgroundColor: '#f5f5f5',
          },
          buttonNext: {
            backgroundColor: '#00838f',
            fontSize: 14,
            padding: '8px 16px',
            borderRadius: 4,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
          buttonBack: {
            color: '#00838f',
            fontSize: 14,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
          buttonSkip: {
            color: '#666',
            fontSize: 14,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
        }}
        locale={{
          back: 'Précédent',
          close: 'Fermer',
          last: 'Terminer',
          next: 'Suivant',
          skip: 'Passer',
        }}
      />

      {/* Sidebar */}
      <Box
        sx={{
          position: 'relative',
          width: isSidebarOpen ? 240 : 48,
          transition: 'width 0.3s',
          overflow: 'visible',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #dee2e6',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >


        {/* Add New Contact button (only when open) */}
        {isSidebarOpen && (
          <Tooltip title="Ajouter un nouveau contact">
            <Button
              data-tourid="new-contact-button"
              variant="contained"
              fullWidth
              onClick={() => navigate('/contacts/new')}
              sx={{ mb: 3, py: 1.5, fontWeight: 600, borderRadius: 8, textTransform: 'none', backgroundColor: '#00838f', '&:hover': { backgroundColor: '#006064' } }}
            >
              <AddIcon />
            </Button>
          </Tooltip>
        )}

        {/* Sidebar menu items – rendered only when open */}
        {isSidebarOpen && (
          <List dense data-tourid="sidebar-filters">
            {sidebarItems.map((item) => (
              <ListItem
                button
                key={item.label}
                onClick={() => handleSidebarItemClick(item)}
                sx={{
                  py: 0.8,
                  backgroundColor: activeFilter === item.filter ? '#e3f2fd' : 'transparent',
                  borderRadius: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: activeFilter === item.filter ? '#bbdefb' : '#f5f5f5'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: activeFilter === item.filter ? '#1976d2' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: activeFilter === item.filter ? 600 : 400,
                    color: activeFilter === item.filter ? '#1976d2' : 'inherit'
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}

      </Box>

      {/* Sidebar Toggle Button */}
      <Box sx={{ ml: '-1px', pt: '40px' }}>
        <IconButton
          data-tourid="sidebar-toggle"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          size="small"
          sx={{
            backgroundColor: '#ffffff',
            right: -20,
            border: '1px solid #dee2e6',
            borderRadius: '50%',
            boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
        >
          {isSidebarOpen ? (
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>

      {/* Main content */}
      <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh', flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom sx={{ mb: 0 }}>
              Contacts
            </Typography>
            <Tooltip title="Guide de démarrage">
              <IconButton
                size="small"
                onClick={() => {
                  // Open sidebar before starting tour to ensure elements are in DOM
                  if (!isSidebarOpen) {
                    setIsSidebarOpen(true);
                    // Wait for sidebar to render before starting tour
                    setTimeout(() => setRunTour(true), 500);
                  } else {
                    setRunTour(true);
                  }
                }}
                sx={{
                  color: '#00838f',
                  '&:hover': { backgroundColor: 'rgba(0, 131, 143, 0.08)' },
                  p: 0.5,
                  ml: 1
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Favorites filter indicator */}
          {activeFilter === 'favorites' && (
            <Chip
              icon={<StarIcon sx={{ fontSize: 16 }} />}
              label="Favoris"
              variant="outlined"
              onDelete={() => {
                setActiveFilter('all');
                setCurrentPage(1);
              }}
              sx={{
                ml: 2,
                bgcolor: '#fff9c4',
                color: '#f57f17',
                borderColor: '#f57f17',
                '& .MuiChip-deleteIcon': {
                  color: '#f57f17'
                },
                '& .MuiChip-icon': {
                  color: '#f57f17'
                }
              }}
            />
          )}

          {/* Segment filter indicator */}
          {segmentFilter && (
            <Chip
              label={`Segment: ${segmentName}`}
              variant="outlined"
              onDelete={() => {
                setSearchParams({});
                setSegmentFilter(null);
                setSegmentName(null);
              }}
              sx={{
                ml: 2,
                bgcolor: '#e3f2fd',
                color: '#1976d2',
                borderColor: '#1976d2',
                '& .MuiChip-deleteIcon': {
                  color: '#1976d2'
                }
              }}
            />
          )}
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {/* Bulk Actions */}
            <FormControl size="small" sx={{ minWidth: 180 }} data-tourid="bulk-actions">
              <InputLabel>Actions groupées</InputLabel>
              <Select
                value={bulkDeleteAction}
                label="Actions groupées"
                disabled={selectedRows.length === 0}
                onChange={handleBulkActionChange}
              >
                <MenuItem value="delete">Supprimer ({selectedRows.length})</MenuItem>
              </Select>
            </FormControl>


            {/* Export */}
            <Button
              variant="outlined"
              size="small"
              sx={{ ml: 2 }}
              disabled={selectedRows.length === 0}
              onClick={handleExportCSV}
            >
              Exporter en CSV
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ml: 2 }}
              onClick={() => navigate('/contacts/import')}
            >
              Importer
            </Button>

            {/* Search */}
            <TextField
              data-tourid="search-contacts"
              placeholder="Rechercher par nom, email ou téléphone..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ ml: 'auto', minWidth: 250 }}
            />

            {/* View and Pagination Info */}
            <Typography variant="body2" sx={{ ml: 2 }}>
              Afficher
            </Typography>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2">
              {totalRows > 0 ? `${startIndex} – ${endIndex} sur ${totalRows}` : '0 contacts'}
            </Typography>
          </Box>
        </Paper>

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error message */}
        {error && (
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: '#ffebee' }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Paper>
        )}

        {/* Data Grid */}
        {!loading && (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }} data-tourid="contacts-grid">
            <StyledDataGrid
              rows={profiles}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.itemId}
              disableSelectionOnClick
              checkboxSelection={false}
              autoHeight
              hideFooter
              getRowHeight={() => 'auto'}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              isCellEditable={() => false}
            />

            {/* Custom Pagination */}
            <Box data-tourid="pagination" sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderTop: '1px solid #dee2e6',
              backgroundColor: '#f8f9fa'
            }}>
              <Typography variant="body2" color="text.secondary">
                Affichage de {totalRows > 0 ? `${startIndex} - ${endIndex}` : '0'} sur {totalRows} contacts
              </Typography>

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  disabled={loading}
                />
              )}
            </Box>
          </Paper>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }
          }}
        >
          <DialogTitle sx={{
            pb: 1,
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#333'
          }}>
            {isDeleting ? "Suppression en cours..." : "Confirmer la suppression"}
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            {isDeleting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, flexDirection: 'column' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, color: '#555' }}>
                  Suppression du contact...
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="body1" sx={{ mb: 2, color: '#555' }}>
                  Êtes-vous sûr de vouloir supprimer le contact{' '}
                  <strong>
                    {profileToDelete?.properties?.firstName || ''} {profileToDelete?.properties?.lastName || 'Unknown'}
                  </strong>{' '}
                  ?
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                  Cette action est irréversible.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              disabled={isDeleting}
              sx={{
                mr: 2,
                textTransform: 'uppercase',
                fontWeight: 600,
                color: '#666',
                borderColor: '#ddd',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              disabled={isDeleting}
              sx={{
                textTransform: 'uppercase',
                fontWeight: 600,
                backgroundColor: '#dc3545',
                '&:hover': {
                  backgroundColor: '#c82333'
                },
                minWidth: 120, // Ensure button size is consistent
              }}
            >
              {isDeleting ? <CircularProgress size={24} color="inherit" /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Delete Progress Dialog */}
        <Dialog
          open={bulkDeleteDialogOpen}
          onClose={handleBulkDeleteCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }
          }}
        >
          <DialogTitle sx={{
            pb: 2,
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#333',
            borderBottom: '1px solid #dee2e6'
          }}>
            {isBulkDeleting ? "Suppression en cours..." : bulkDeletionProgress.length > 0 ? "Résultats de la suppression" : "Confirmer la suppression groupée"}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {bulkDeletionProgress.length === 0 ? (
              <>
                <Typography variant="body1" sx={{ mb: 2, color: '#555' }}>
                  Êtes-vous sûr de vouloir supprimer <strong>{selectedRows.length} contact(s)</strong> ?
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                  Cette action est irréversible et supprimera les contacts.
                </Typography>
              </>
            ) : (
              <Box>
                {/* Overall Progress */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Progression: {bulkDeletionProgress.filter(p => p.status === 'success').length} / {bulkDeletionProgress.length} contacts supprimés
                  </Typography>
                  {isBulkDeleting && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Traitement en cours...
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Individual Results */}
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {bulkDeletionProgress.map((result, index) => (
                    <Paper
                      key={result.profile_id}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: '1px solid #dee2e6',
                        borderRadius: 1,
                        backgroundColor:
                          result.status === 'success' ? '#f0f9ff' :
                            result.status === 'error' ? '#fff5f5' :
                              '#ffffff'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {/* Status Icon */}
                        <Box sx={{ mt: 0.5 }}>
                          {result.status === 'success' && (
                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                          )}
                          {result.status === 'error' && (
                            <ErrorIcon sx={{ color: '#f44336', fontSize: 24 }} />
                          )}
                          {result.status === 'pending' && (
                            isBulkDeleting ? (
                              <CircularProgress size={24} sx={{ color: '#1976d2' }} />
                            ) : (
                              <PendingIcon sx={{ color: '#9e9e9e', fontSize: 24 }} />
                            )
                          )}
                        </Box>

                        {/* Contact Info and Status */}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                            {result.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {result.email}
                          </Typography>

                          {/* Status */}
                          <Typography variant="caption" sx={{
                            color: result.status === 'success' ? '#4caf50' : result.status === 'error' ? '#f44336' : '#9e9e9e',
                            display: 'block',
                            mb: 0.5
                          }}>
                            {result.status === 'success' ? '✓ Supprimé' : result.status === 'error' ? '✗ Échec' : 'En attente'}
                          </Typography>

                          {/* Error Message */}
                          {result.error && (
                            <Typography variant="caption" sx={{ color: '#f44336', display: 'block', mt: 1 }}>
                              Erreur: {result.error}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #dee2e6' }}>
            {bulkDeletionProgress.length === 0 ? (
              <>
                <Button
                  onClick={handleBulkDeleteCancel}
                  variant="outlined"
                  sx={{
                    mr: 2,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    color: '#666',
                    borderColor: '#ddd',
                    '&:hover': {
                      borderColor: '#999',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleBulkDeleteConfirm}
                  variant="contained"
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    backgroundColor: '#dc3545',
                    '&:hover': {
                      backgroundColor: '#c82333'
                    },
                    minWidth: 120,
                  }}
                >
                  Supprimer
                </Button>
              </>
            ) : (
              <Button
                onClick={handleBulkDeleteCancel}
                variant="contained"
                disabled={isBulkDeleting}
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  minWidth: 120,
                }}
              >
                Fermer
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Context Menu */}
        <Menu
          open={menuPosition !== null}
          onClose={handleMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            menuPosition !== null
              ? { top: menuPosition.mouseY, left: menuPosition.mouseX }
              : undefined
          }
          PaperProps={{
            sx: {
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              minWidth: 180,
            }
          }}
        >
          <MenuItem
            onClick={() => {
              navigate(`/contacts/${selectedProfile?.itemId}`);
              handleMenuClose();
            }}
            sx={{ py: 1.5, px: 2 }}
          >
            <VisibilityIcon sx={{ mr: 2, fontSize: 18, color: '#6c757d' }} />
            <Typography variant="body2">Voir les détails</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate(`/contacts/${selectedProfile?.itemId}/edit`);
              handleMenuClose();
            }}
            sx={{ py: 1.5, px: 2 }}
          >
            <EditIcon sx={{ mr: 2, fontSize: 18, color: '#6c757d' }} />
            <Typography variant="body2">Modifier</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleDeleteClick(selectedProfile)}
            sx={{
              py: 1.5,
              px: 2,
              color: '#dc3545',
              '&:hover': {
                backgroundColor: '#fff5f5'
              }
            }}
          >
            <DeleteIcon sx={{ mr: 2, fontSize: 18, color: '#dc3545' }} />
            <Typography variant="body2" color="#dc3545">Supprimer le contact</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default Contacts;